'use client';

import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { useCallback, useMemo } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { CustomTooltip } from '../../../../components/CustomTooltip';
import { Skeleton } from '../../../../components/ui/skeleton';
import { getDeclinedMonthName } from '../../lib/utils';

interface DailyCurrencyData {
  date: string;
  fullDate: Date;
  value: number;
  change?: number;
}

interface CurrencyChartProps {
  chartData: DailyCurrencyData[];
  loading: boolean;
  currencyName: string | undefined;
  monthName: string;
  year: number;
}

export function CurrencyChartMonth({ chartData, loading, currencyName, year }: CurrencyChartProps) {
  // ✅ ОПТИМИЗАЦИЯ 1: useMemo для расчётов
  const { sortedChartData, domainMin, domainMax, declinedMonthName } = useMemo(() => {
    if (!chartData || chartData.length === 0) {
      const now = new Date();
      const monthIdx = now.getMonth();
      const monthStr = getDeclinedMonthName(monthIdx, 'prepositional');
      return {
        sortedChartData: [],
        domainMin: 0,
        domainMax: 1,
        declinedMonthName: monthStr,
      };
    }

    const data = chartData.map((item) => ({
      ...item,
      fullDate: new Date(item.fullDate).getTime(),
    }));

    const values = data.map((d) => d.value);
    let minY = Math.min(...values);
    let maxY = Math.max(...values);

    if (minY === maxY) {
      minY *= 0.999;
      maxY *= 1.001;
    } else {
      const padding = (maxY - minY) * 0.1;
      minY -= padding;
      maxY += padding;
    }

    const monthIdx = chartData[0].fullDate.getMonth();
    const monthStr = getDeclinedMonthName(monthIdx, 'prepositional');

    return {
      sortedChartData: data,
      domainMin: Math.floor(minY * 100) / 100,
      domainMax: Math.ceil(maxY * 100) / 100,
      declinedMonthName: monthStr,
    };
  }, [chartData]);

  // ✅ useMemo для тултипа
  const memoizedTooltip = useMemo(
    () => <CustomTooltip currencyName={currencyName || 'Курс'} />,
    [currencyName],
  );

  // ✅ useCallback для форматтеров осей
  const formatXAxis = useCallback(
    (value: number) => format(new Date(value), 'dd.MM', { locale: ru }),
    [],
  );
  const formatYAxis = useCallback((tick: number) => tick.toFixed(2), []);

  return (
    <section
      aria-labelledby="currency-chart-title"
      className="w-full max-w-full mx-auto rounded-xl shadow-lg p-4 sm:p-6 bg-slate-800/50 backdrop-blur-sm border border-slate-700 dark:bg-gradient-to-br dark:from-slate-800/50 dark:via-slate-700/60 dark:to-slate-800/50 dark:border dark:border-slate-700 flex flex-col h-full"
    >
      <header className="mb-4">
        <h2
          id="currency-chart-title"
          className="text-lg sm:text-xl font-semibold text-main"
          aria-live="polite"
        >
          {loading ? (
            <Skeleton className="w-72 h-6" />
          ) : (
            `График динамики курса ${currencyName || ''} в ${declinedMonthName} ${year} года`
          )}
        </h2>
      </header>

      <figure
        aria-label={`График изменения курса ${currencyName} за ${declinedMonthName} ${year}`}
        role="img"
      >
        {loading ? (
          <Skeleton className="w-full h-72 sm:h-80" />
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={sortedChartData} margin={{ top: 20, right: 30, left: 20, bottom: 30 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid-color)" />
              <XAxis
                dataKey="fullDate"
                type="number"
                scale="time"
                stroke="#cbd5e0"
                domain={['dataMin', 'dataMax']}
                tickMargin={10}
                angle={0}
                tickFormatter={formatXAxis}
              />
              <YAxis
                domain={[domainMin, domainMax]}
                stroke="#cbd5e0"
                tickFormatter={formatYAxis}
                tickMargin={10}
                padding={{ top: 10, bottom: 10 }}
              />
              <Tooltip content={memoizedTooltip} />
              <Line
                type="monotone"
                dataKey="value"
                stroke="var(--chart-line-color)"
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        )}

        {!loading && (
          <figcaption className="mt-3 text-sm text-gray-400 text-center sm:text-left">
            Источник: официальный курс {currencyName} по дням в {declinedMonthName} {year} года.
          </figcaption>
        )}
      </figure>
    </section>
  );
}
